import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@/components/Button';
import PasswordInput from '@/components/PasswordInput';
import PasswordRequirements from '@/components/PasswordRequirements';
import changePassIcon from '@/assets/change_pass.svg';
import changeSuccessIcon from '@/assets/change_success.svg';
import { changePassword, logout } from '@/api/auth';
import useAuthStore from '@/stores/authStore';
import { validatePassword } from '@/utils/validationUtils';

const ChangePasswordDialog = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  
  const validateForm = () => {
    const newErrors = {};
    
    // Check password
    if (!password) {
      newErrors.password = t('auth.signup.errors.password_required');
    } else {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.length) {
        newErrors.password = t('auth.signup.errors.password_length');
      } else if (!passwordValidation.uppercase) {
        newErrors.password = t('auth.signup.errors.password_uppercase');
      } else if (!passwordValidation.number) {
        newErrors.password = t('auth.signup.errors.password_number');
      }
    }
    
    // Check confirm password
    if (!confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.confirm_password_required');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.passwords_not_match');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    try {
      await changePassword(password);
      setIsSuccess(true);
    } catch (error) {
      console.error('Password change failed:', error);
      setErrors({
        general: t('user.errors.password_change_failed')
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleConfirmSuccess = () => {
    // Logout the user
    logout().finally(() => {
      // Invalidate all queries in the cache
      queryClient.clear();
      
      // Clear authentication state
      clearAuth();
      
      // Navigate to home page
      navigate('/');
    });
  };
  
  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[460px] bg-white rounded-xl flex flex-col items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changeSuccessIcon} alt="Success" className="w-full h-full" />
            </div>
            <div className="w-[263px] flex flex-col items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('user.password.success_title')}
              </div>
              <div className="self-stretch text-center text-[#666666] text-sm leading-[21px]">
                {t('user.password.success_message')}
              </div>
            </div>
          </div>
          <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch flex justify-start items-center gap-3">
              <Button 
                variant="primary" 
                className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleConfirmSuccess}
              >
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="w-[460px] bg-white rounded-xl flex flex-col items-center overflow-hidden">
        <div className="self-stretch px-6 pt-8 pb-6 flex flex-col items-center gap-4">
          <div className="w-12 h-12 relative">
            <img src={changePassIcon} alt="Change Password" className="w-full h-full" />
          </div>
          <div className="w-[263px] flex flex-col items-center gap-2">
            <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
              {t('user.password.title')}
            </div>
          </div>
          <div className="self-stretch flex flex-col items-start gap-2">
            <div className="self-stretch flex flex-col items-start gap-1">
              <div className="self-stretch flex items-start gap-3">
                <div className="flex-1 flex items-center gap-1">
                  <div className="text-[#111111] text-sm leading-[21px]">
                    {t('user.password.new_password')}
                  </div>
                </div>
              </div>
              <PasswordInput
                placeholder={t('user.password.password_placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                containerClassName="self-stretch"
                className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
              >
                <PasswordRequirements password={password} />
              </PasswordInput>
            </div>
          </div>
          <div className="self-stretch flex flex-col items-start gap-1">
            <div className="self-stretch flex items-start gap-3">
              <div className="flex-1 flex items-center gap-1">
                <div className="text-[#111111] text-sm leading-[21px]">
                  {t('user.password.confirm_password')}
                </div>
              </div>
            </div>
            <PasswordInput
              placeholder={t('user.password.confirm_placeholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={errors.confirmPassword}
              containerClassName="self-stretch"
              className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
            />
          </div>
          {errors.general && (
            <div className="text-red-500 text-sm mt-2">
              {errors.general}
            </div>
          )}
        </div>
        <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
          <div className="self-stretch flex items-center gap-3">
            <Button 
              variant="default" 
              className="flex-1 h-12 px-6 bg-white rounded-3xl outline outline-[0.5px] outline-offset-[-0.5px] outline-[#dddddd]"
              onClick={onClose}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              variant="primary" 
              className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('common.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordDialog; 